import { css } from 'styled-components';

import { anchorTokens } from '../../tokens/components/Anchor/tokens';
import { buttonTokens } from '../../tokens/components/Button/tokens';
import { buttonBase, buttonVariants, buttonSizes } from '../Button/styles';
import { dropdownTokens } from '../../tokens/components/Dropdown/tokens';

export const resetBrowserAnchor = css`
  color: inherit;
  text-decoration: none;

  &:hover {
    color: inherit;
    text-decoration: none;
  }
`;

export const anchorCore = css`
  font-family: ${anchorTokens.CoreFontFamily};
  outline-offset: 0;
  transition-duration: 80ms;
  transition-property: box-shadow, color, outline;
  transition-timing-function: ease-out;

  &:focus-visible {
    box-shadow: 0 0 0 ${buttonTokens.ShadowWidthBaseKeyboardFocus} ${buttonTokens.ShadowColorBaseKeyboardFocus};
  }

  ${({ theme }) =>
    theme.disabled &&
    css`
      cursor: default;
      pointer-events: none;

      &:focus-visible {
        outline: none;
      }
    `}
`;

/*
  Styling notes:
  Because Anchor shares so many button styles, the core for link
  applies during the variant so that we won't have to negate properties
  multiple times; i.e. underline hover or text color change.

  Although the rule with tokens is that we don't want to be dependent on
  non-global tokens for styling, all the button variations in this case
  should match exactly. To prevent them from getting out of sync, we
  will do a 1-way reference; in this case, we are pointing to Button
  from Anchor but Button "link" does not reference Anchor to avoid
  circular dependency.
*/

export const anchorVariants = {
  aura: css`
    ${buttonBase};
    ${buttonSizes.auto};
    ${buttonVariants.aura};
  `,
  danger: css`
    ${buttonBase};
    ${buttonSizes.auto};
    ${buttonVariants.danger};
  `,
  btn: css`
    // same as default Button
    ${buttonBase};
    ${buttonSizes.auto};
    ${buttonVariants.default};
  `,
  inverse: css`
    ${buttonBase};
    ${buttonSizes.auto};
    ${buttonVariants.inverse};
  `,
  link: css`
    ${buttonVariants.link}
  `,
  primary: css`
    ${buttonBase};
    ${buttonSizes.auto};
    ${buttonVariants.primary};
  `,
  transparent: css`
    ${buttonBase};
    ${buttonSizes.auto};
    ${buttonVariants.transparent};
  `,
  underline: css`
    ${buttonVariants.link}
    color: ${anchorTokens.UnderlineTextColorInitial};
    display: inline-block;
    text-decoration: ${anchorTokens.UnderlineTextDecorationDefault};
    text-underline-offset: 0.15em;

    &:hover {
      color: ${anchorTokens.UnderlineTextColorHover};
    }

    &:active {
      color: ${anchorTokens.UnderlineTextColorActive};
    }

    ${({ theme }) =>
      theme.disabled &&
      css`
        color: ${anchorTokens.UnderlineTextColorDisabled};
      `}
  `,
};

export const anchorSizes = {
  block: css`
    display: flex;
    padding-left: 0;
    padding-right: 0;
    width: 100%;
  `,
  cell: css`
    ${buttonSizes.cell};
  `,
  dropdown: css`
    // TODO: Move these styles since they do more than set anchor size
    text-decoration: ${anchorTokens.DropdownTextDecorationDefault};
    color: ${anchorTokens.DropdownTextColorInitial};
    width: 100%;
    height: 100%;
    padding: ${({ theme }) =>
      theme.mobile
        ? `${dropdownTokens.ItemSpacingVerticalBaseDefaultMobile} ${dropdownTokens.ItemSpacingHorizontalBaseDefault}`
        : `${dropdownTokens.ItemSpacingVerticalBaseDefaultDesktop} ${dropdownTokens.ItemSpacingHorizontalBaseDefault}`};

    &:hover,
    &:focus-visible {
      color: ${dropdownTokens.ItemFontColorBaseHover};

      ${({ theme }) =>
        theme.hover &&
        !theme.disabled &&
        css`
          background-color: ${dropdownTokens.ItemBackgroundColorBaseHover};
          box-shadow: none;
        `}
    }
    &:hover {
      text-decoration: ${anchorTokens.DropdownTextDecorationDefault};
    }

    // Dropdown.Anchor has a different, custom, focus visual
    // Double ampersand specificity boots needed to override hover styles
    &:focus-visible,
    &&:focus-visible:hover {
      border-radius: 0;
      outline: ${dropdownTokens.ItemOutlineStrokeBaseKeyboardFocus} ${dropdownTokens.ItemOutlineColorBaseKeyboardFocus};
      outline-offset: ${dropdownTokens.ItemOutlineOffsetBaseKeyboardFocus};
    }

    ${({ theme }) =>
      theme.disabled &&
      css`
        color: ${anchorTokens.UnderlineTextColorDisabled};
        pointer-events: none;

        label:hover:has(> &),
        label:focus:has(> &) {
          background-color: transparent;
          cursor: default;
        }

        label:hover > &&,
        label:focus > &&,
        &:hover,
        &:focus {
          color: ${anchorTokens.UnderlineTextColorDisabled};
        }

        &:focus-visible {
          outline: none;
        }
      `}
  `,
  large: css`
    // Variant should be deprecated
  `,
  mini: css`
    // Variant should be deprecated (does not exist in refresh)
    ${buttonSizes.mini};
  `,
  small: css`
    ${buttonSizes.small};
  `,
  wide: css`
    ${buttonSizes.wide};
  `,
};

export const anchorPresets = {
  'new-tab': css`
    &::after {
      content: '${anchorTokens.NewTabIcon}';
      display: inline-block;
      line-height: 1;
      margin-left: 4px;
    }
  `,
  forward: css`
    &::after {
      content: '${anchorTokens.ForwardIcon}';
      display: inline-block;
      line-height: 1;
      margin-left: ${anchorTokens.IconSpacing};
    }
  `,
  backward: css`
    &::before {
      content: '${anchorTokens.BackwardIcon}';
      display: inline-block;
      line-height: 1;
      margin-right: ${anchorTokens.IconSpacing};
    }
  `,
  download: css`
    &::after {
      content: '${anchorTokens.DownloadIcon}';
      display: inline-block;
      line-height: 1;
      margin-left: ${anchorTokens.IconSpacing};
    }
  `,
};
